import { FiUsers } from 'react-icons/fi';

export const multipleSpeakersData = {
  courseId: 'multiple-speakers',
  courseType: 'listening-course',
  title: 'Multiple Speakers',
  icon: FiUsers,
  overview: {
    impact: 'Track who says what in group discussions and accurately match speakers to their opinions',
    focus: 'Distinguishing 2–4 speakers by voice, role, and opinion; tracking agreement, disagreement, and turn-taking in academic discussions',
    uses: 'IELTS Listening Section 3 (academic discussion between 2–4 speakers)',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Understanding Multi-Speaker Sections',
      content: `<p><strong>Section 3</strong> features a conversation between <strong>two to four speakers</strong> in an academic context — typically students discussing an assignment, project, or presentation, sometimes with a tutor. Unlike Sections 1–2, the language here is more complex, with speakers debating ideas, giving opinions, and negotiating decisions.</p>
<p>The question types include <strong>matching speakers to opinions</strong>, <strong>multiple choice</strong>, and <strong>classification</strong> (e.g., "Who says the following?"). These questions require you to track not just <em>what</em> is said, but <em>who</em> says it — and whether they agree or disagree with each other.</p>`,
      points: [
        'Section 3 has 10 questions and is considered more difficult than Sections 1–2.',
        'Speakers are usually introduced by name at the start — note down their names immediately.',
        'The conversation involves opinions, not just facts — listen for hedging, qualifiers, and attitude.',
        'Questions may ask you to match a speaker to an opinion they DON\'T hold — read questions carefully.',
        'Paraphrasing is heavy: the question uses different words from what the speaker actually says.',
      ],
      warning: 'A speaker may present someone else\'s opinion before giving their own. Don\'t attribute the first opinion you hear to the speaker — wait for their personal view.',
    },
    {
      title: 'Distinguishing Speakers',
      content: `<p>With multiple speakers, you need reliable strategies to tell them apart. IELTS helps by giving speakers <strong>different voice characteristics</strong> (male/female, pitch, accent), but when speakers are the same gender, you must rely on <strong>contextual clues</strong>.</p>
<p>Speakers are distinguished by their <strong>role</strong> (student vs. tutor, researcher vs. reviewer), their <strong>position on the topic</strong> (for or against), and their <strong>speaking patterns</strong> (one may be more assertive, another more tentative). The tutor usually asks questions and summarises; students present findings and debate.</p>`,
      points: [
        'Note each speaker\'s name at the start of the audio — write them on your question paper.',
        'A tutor/professor tends to ask guiding questions and make final summaries.',
        'Students often present their research and defend their choices.',
        'Listen for name-calling: "What do you think, Sarah?" signals the next speaker is Sarah.',
        'If two speakers agree, one may say "Yes, and…" or "I was thinking the same thing."',
        'If a speaker is unsure, they use hedging: "I\'m not sure, but…" or "It might be…"',
      ],
      examples: [
        { label: 'Role identification', text: '"Professor, we chose to focus on renewable energy." → The speaker is a student; the professor is being addressed.' },
        { label: 'Named turn', text: '"That\'s interesting, Tom. And what about you, Lisa — do you agree?" → Lisa speaks next.' },
      ],
    },
    {
      title: 'Agreement and Disagreement Language',
      content: `<p>A central skill in Section 3 is recognising whether speakers <strong>agree</strong>, <strong>partially agree</strong>, or <strong>disagree</strong> with each other. IELTS often tests this directly: "Which TWO speakers agree about…?" or "Who disagrees with the proposal?"</p>
<p>Agreement and disagreement exist on a <strong>spectrum</strong>. Strong agreement ("Absolutely!") is easy to spot, but partial agreement ("Yes, but…") and polite disagreement ("I see your point, however…") are more subtle and more commonly tested.</p>`,
      points: [
        'Strong agreement: "Exactly," "I completely agree," "Absolutely," "That\'s right."',
        'Partial agreement: "Yes, but…," "I see what you mean, but…," "To some extent."',
        'Polite disagreement: "I\'m not sure about that," "I take your point, however…," "Don\'t you think…?"',
        'Strong disagreement: "I disagree," "I don\'t think that\'s right," "Actually, I think…"',
        'Conceding a point: "Fair enough," "You may have a point," "I suppose so."',
      ],
      table: {
        headers: ['Level', 'Phrases', 'Signal'],
        rows: [
          ['Strong agreement', '"Exactly!" / "I completely agree." / "That\'s a great point."', 'Speakers share the same opinion without reservation'],
          ['Partial agreement', '"Yes, but…" / "I agree to an extent, however…" / "That\'s true, although…"', 'The speaker acknowledges the point but adds a qualification or concern'],
          ['Polite disagreement', '"I\'m not sure I agree." / "I see your point, but…" / "Don\'t you think it might be…?"', 'The speaker avoids direct confrontation but holds a different view'],
          ['Strong disagreement', '"I disagree." / "I don\'t think so." / "Actually, the evidence suggests…"', 'Clear opposition — the speaker directly states a contrary position'],
          ['Concession', '"Fair enough." / "You may be right." / "I hadn\'t thought of that."', 'The speaker changes or softens their original position'],
        ],
      },
    },
    {
      title: 'Strategies for Speaker-Matching Questions',
      content: `<p>Speaker-matching questions ask you to link each opinion, action, or statement to the correct speaker. These require <strong>active tracking</strong> throughout the conversation — you can't just listen passively.</p>
<p>The key challenge is that speakers often <strong>discuss</strong> an opinion before it becomes clear who holds it. One speaker may introduce an idea, another may react, and a third may give the definitive opinion. You need to follow the full exchange, not just grab the first statement.</p>`,
      points: [
        'Read all the statements BEFORE the audio starts — underline keywords in each one.',
        'As you listen, write the speaker\'s initial (S, T, L…) next to each statement as you hear it attributed.',
        'Watch for "I think" and "In my opinion" — these directly attribute an opinion to the speaker.',
        'If a speaker says "You think X, but I think Y" — attribute Y to the current speaker and X to the person addressed.',
        'Be wary of reported speech: "Professor Smith said…" is reporting someone else\'s view, not the speaker\'s own.',
        'Cross out options as you use them if each speaker can only be matched once.',
      ],
      warning: 'Don\'t confuse what a speaker REPORTS with what they BELIEVE. "The textbook says X" doesn\'t mean the speaker agrees with X.',
      examples: [
        { label: 'Attribution', text: '"I read that the population doubled, but personally I think that figure is exaggerated." → The speaker\'s opinion: the figure is exaggerated.' },
        { label: 'Indirect clue', text: '"Lisa, you were the one who suggested we change the methodology, weren\'t you?" → Attribute the methodology change to Lisa.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Who Said What',
      type: 'select',
      instructions: 'Read each conversation excerpt. Determine which speaker holds each opinion.',
      questions: [
        {
          text: 'Tom: "I think we should focus on climate change for our presentation." Lisa: "That\'s quite broad. How about narrowing it to deforestation?" Tom: "Good point — let\'s go with that." Who suggested the final topic?',
          options: ['Tom', 'Lisa', 'Both equally', 'Neither — it was the tutor'],
          answer: 'Lisa',
          explanation: 'Lisa proposed deforestation, and Tom agreed. The final topic was Lisa\'s suggestion.',
        },
        {
          text: 'Professor: "How did you find the survey method?" Sarah: "It worked well for quantitative data." Mark: "I\'m not so sure — the sample size was too small to be reliable." Who is critical of the survey method?',
          options: ['The Professor', 'Sarah', 'Mark', 'Both Sarah and Mark'],
          answer: 'Mark',
          explanation: 'Mark expresses doubt ("I\'m not so sure") and cites a limitation. Sarah was positive about it.',
        },
        {
          text: 'Anna: "According to the article, social media improves communication." Ben: "That\'s what it claims, but I don\'t buy it." Anna: "Neither do I, actually. The evidence was weak." Who believes social media improves communication?',
          options: ['Anna', 'Ben', 'Neither Anna nor Ben', 'Both Anna and Ben'],
          answer: 'Neither Anna nor Ben',
          explanation: 'Anna was reporting the article\'s claim, not her own view. Both Anna and Ben ultimately reject it.',
        },
        {
          text: 'Tutor: "Whose idea was it to include the case study?" Rachel: "That was James\'s suggestion." James: "Yes, I thought it would strengthen the argument." Who should be credited with the case study idea?',
          options: ['The tutor', 'Rachel', 'James', 'All three'],
          answer: 'James',
          explanation: 'Rachel attributes the idea to James, and James confirms it. The tutor only asked the question.',
        },
      ],
    },
    {
      title: 'Match Speakers to Opinions',
      type: 'select',
      instructions: 'Based on the discussion, match each speaker to their stated opinion.',
      questions: [
        {
          text: 'In a discussion about research methods, Priya says: "Interviews give richer data." David says: "But questionnaires reach more people." Priya replies: "True, but depth matters more here." What is Priya\'s opinion?',
          options: [
            'Questionnaires are better because they reach more people.',
            'Interviews are preferable because they provide deeper data.',
            'Both methods are equally good.',
            'Neither method is suitable.',
          ],
          answer: 'Interviews are preferable because they provide deeper data.',
          explanation: 'Priya consistently advocates for interviews and values depth over reach.',
        },
        {
          text: 'Tutor: "Should the team present jointly or individually?" Mia: "Jointly — it shows teamwork." Leo: "I\'d prefer individually. I want my contribution to be clear." What does Leo prefer?',
          options: [
            'Joint presentation',
            'Individual presentation',
            'Written report instead',
            'No preference',
          ],
          answer: 'Individual presentation',
          explanation: 'Leo explicitly says "I\'d prefer individually" and gives his reason.',
        },
        {
          text: 'Kate: "I think the experiment failed because of the temperature variable." Omar: "I agree about the failure, but I think it was the sample contamination." Where do Kate and Omar agree?',
          options: [
            'The experiment was successful.',
            'The experiment failed.',
            'Temperature was the cause.',
            'Contamination was the cause.',
          ],
          answer: 'The experiment failed.',
          explanation: 'Both agree the experiment failed. They disagree on the CAUSE — Kate blames temperature; Omar blames contamination.',
        },
        {
          text: 'Amy: "The results support our hypothesis." Chen: "Do they? I think they\'re inconclusive." Tutor: "Chen has a point — you need a larger dataset." Who considers the results inconclusive?',
          options: ['Amy only', 'Chen only', 'Chen and the tutor', 'All three speakers'],
          answer: 'Chen and the tutor',
          explanation: 'Chen directly says the results are inconclusive. The tutor supports Chen\'s view by saying a larger dataset is needed.',
        },
      ],
    },
    {
      title: 'Identify Agreement or Disagreement',
      type: 'select',
      instructions: 'Determine whether the second speaker agrees, partially agrees, or disagrees with the first.',
      questions: [
        {
          text: 'Speaker A: "Online learning is just as effective as face-to-face." Speaker B: "I take your point, but I think practical subjects need in-person teaching."',
          options: ['Full agreement', 'Partial agreement', 'Polite disagreement', 'Strong disagreement'],
          answer: 'Partial agreement',
          explanation: '"I take your point, but…" acknowledges validity while adding a qualification — classic partial agreement.',
        },
        {
          text: 'Speaker A: "We should extend the deadline by two weeks." Speaker B: "Absolutely. We\'ll need every extra day."',
          options: ['Full agreement', 'Partial agreement', 'Polite disagreement', 'Strong disagreement'],
          answer: 'Full agreement',
          explanation: '"Absolutely" and reinforcing the point with "every extra day" shows strong, unqualified agreement.',
        },
        {
          text: 'Speaker A: "The marketing budget should be cut in half." Speaker B: "I really don\'t think that\'s a good idea. We\'d lose too much visibility."',
          options: ['Full agreement', 'Partial agreement', 'Polite disagreement', 'Strong disagreement'],
          answer: 'Strong disagreement',
          explanation: '"I really don\'t think that\'s a good idea" is direct opposition, reinforced by a reason against it.',
        },
        {
          text: 'Speaker A: "Renewable energy is too expensive." Speaker B: "I see what you mean, but costs have dropped significantly in the last five years."',
          options: ['Full agreement', 'Partial agreement', 'Polite disagreement', 'Strong disagreement'],
          answer: 'Polite disagreement',
          explanation: '"I see what you mean, but…" is a politeness marker before presenting a counter-argument. Speaker B is disagreeing while acknowledging A\'s perspective.',
        },
      ],
    },
    {
      title: 'Complete a Discussion Summary',
      type: 'fill-in',
      instructions: 'Based on the discussion excerpt, fill in the summary with the correct information.',
      questions: [
        {
          text: 'Tom and Yuki are discussing their group project with Dr. Harris. Tom says they should use case studies; Yuki prefers statistical analysis. Dr. Harris says: "Why not combine both approaches?" They all agree. Summary: The group decided to use ___ for their project.',
          answer: 'both case studies and statistical analysis',
          explanation: 'Dr. Harris proposed combining both methods, and all speakers agreed. The final decision is the combined approach.',
        },
        {
          text: 'Meg says: "I\'ll handle the literature review." Sam says: "Fine, then I\'ll do the data collection." The tutor adds: "And make sure you both contribute to the conclusion." Meg is responsible for ___.',
          answer: 'the literature review',
          explanation: 'Meg explicitly volunteers for the literature review. Sam takes data collection. Both share the conclusion.',
        },
        {
          text: 'In their discussion, Rosa argues the study is limited by its small sample size. Jack disagrees, saying the qualitative depth compensates. Their professor sides with Rosa. The professor believes the study is limited by ___.',
          answer: 'its small sample size',
          explanation: 'The professor "sides with Rosa," who argued the limitation was the small sample size.',
        },
      ],
    },
    {
      title: 'Predict Speaker Responses',
      type: 'writing',
      instructions: 'Based on the context and the speakers\' established positions, predict how each speaker would respond to the new question.',
      questions: [
        {
          text: 'Context: In a study group, Aisha favours qualitative research (interviews, observations) while Daniel prefers quantitative methods (surveys, statistics). Their tutor asks: "For your next project on student well-being, which method would be most appropriate?" Predict how Aisha and Daniel would each respond, and suggest a compromise.',
          example: 'Aisha might say: "I think interviews would give us real insight into how students feel — numbers can\'t capture emotions." Daniel might respond: "But a survey of 500 students gives us statistically reliable trends." A compromise: use a mixed-methods approach — a large-scale survey supported by 10–15 in-depth interviews.',
          tip: 'Show that you understand each speaker\'s consistent position, then find common ground. In IELTS, discussions often end in compromise.',
        },
        {
          text: 'Context: Three students — Fatima, George, and Lily — are planning a presentation. Fatima is very organised and wants to rehearse three times. George is confident and thinks one run-through is enough. Lily is nervous and hasn\'t decided. The tutor says: "You only have two days left. How will you prepare?" Predict each speaker\'s likely response.',
          example: 'Fatima: "I think we need at least two full rehearsals — one tomorrow and one the morning of." George: "One rehearsal is fine — we know our material." Lily: "I\'d feel more comfortable with two rehearsals, actually." The likely outcome: two rehearsals, as Fatima and Lily outnumber George.',
          tip: 'Track each speaker\'s established personality trait (organised, confident, nervous) and predict consistently. IELTS often resolves group disagreements by majority or compromise.',
        },
      ],
    },
  ],
  tips: [
    'Write down speaker names (or initials) at the very start of the audio — you\'ll need them to match opinions.',
    'If speakers are the same gender, focus on their ROLE (student vs. tutor) and POSITION (for vs. against) to tell them apart.',
    'Watch for "I think," "In my view," "Personally" — these phrases directly attribute an opinion to the current speaker.',
    'Don\'t confuse reported speech ("The textbook says…") with personal opinion. Only attribute what the speaker personally believes.',
    'When two speakers discuss an idea, wait for both to finish before deciding who holds which view.',
    'Practise with group discussion podcasts — pause after each exchange and identify each speaker\'s opinion.',
  ],
  dailyPractice: [
    'Listen to a panel discussion or debate podcast (e.g., BBC The Moral Maze) and note each speaker\'s name and main opinion.',
    'Watch a 5-minute group discussion video — pause every 30 seconds and write who said what.',
    'Practise with IELTS Section 3 recordings: play, pause, and match speakers to opinions before checking answers.',
    'Role-play a group discussion with friends — one person observes and tracks who holds which opinion.',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Multiple Speakers: An intermediate-level (difficulty 3) IELTS Listening skill course covering Section 3 academic discussions with 2–4 speakers. Topics include distinguishing speakers by voice, role, and opinion; tracking agreement, disagreement, partial agreement, and concession; handling interruptions and turn-taking; matching speakers to opinions; and recognising reported speech vs. personal views. Includes 17 practice questions across select, fill-in, and writing exercise types.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...multipleSpeakersData,
      title: multipleSpeakersData.title + ' (Foundations)',
      overview: { ...multipleSpeakersData.overview, duration: '15-20 minutes' },
      sections: multipleSpeakersData.sections.slice(0, Math.max(2, Math.ceil(multipleSpeakersData.sections.length / 2))),
      exercises: multipleSpeakersData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...multipleSpeakersData,
      title: multipleSpeakersData.title + ' (Advanced)',
      overview: { ...multipleSpeakersData.overview, duration: '30-35 minutes' },
      sections: multipleSpeakersData.sections,
      exercises: multipleSpeakersData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(multipleSpeakersData.tips || []),
        'For Band 8+: Train yourself to understand speakers at fast natural speed.',
        'Advanced tip: Practice with varied accents and complex academic content.',
      ],
    };
  }
  return multipleSpeakersData;
}
